import { useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import path from 'path';
import { useTranslation } from 'react-i18next';
import { startOfToday } from 'date-fns/startOfToday';
import { isAfter } from 'date-fns/isAfter';

import type { Option } from '@bloomays-lib/ui.shared';
import { ISociety } from '@bloomays-lib/types.shared';
import {
  getSocietyKBISLabel,
  getSocietyTaxesLabel,
  getSocietyInsuranceLabel,
  hasIbanSupport,
} from '@bloomays-lib/utils.shared';

import { Logger } from '../../services/serviceLogger';
import {
  getSupplierSocietyFormConfig,
  getSupplierStatusOptions,
  deriveExpirationRequired,
  normalizeSupplierSocietyForSubmit,
  type SupplierSocietyFormConfig,
} from '../../helpers/societyForm';

const logger = Logger('useBloomerSocietyForm');

const LS_EXPIRATION_DATES_KEY = 'bloomer-onboarding-expiration-dates';

/**
 * État optionnellement persisté en localStorage.
 * - `persist=true` (créa / onboarding) : lecture au montage puis écriture à chaque changement —
 *   équivalent fonctionnel de `useCustomLocalStorage` (même clé, même sérialisation JSON), sans la
 *   synchro multi-onglets dont le formulaire ne dépend pas.
 * - `persist=false` (tableau de bord V2) : état purement en mémoire, AUCUN accès au localStorage.
 * Les deux branches appellent toujours `useState` + `useEffect` (règles des hooks respectées).
 */
const usePersistentState = <T>(key: string, initial: T, persist: boolean): [T, Dispatch<SetStateAction<T>>] => {
  const [value, setValue] = useState<T>(() => {
    if (!persist || typeof window === 'undefined') return initial;
    try {
      const item = window.localStorage.getItem(key);
      return item ? (JSON.parse(item) as T) : initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    if (!persist || typeof window === 'undefined') return;
    try {
      if (value !== undefined) window.localStorage.setItem(key, JSON.stringify(value));
      else window.localStorage.removeItem(key);
    } catch {
      /* localStorage indisponible : on ignore (le formulaire fonctionne en mémoire). */
    }
  }, [key, value, persist]);
  return [value, setValue];
};

export type UseBloomerSocietyFormParams = {
  society?: ISociety;
  onValidated: (data: ISociety) => Promise<ISociety | undefined>;
  /** Persister le brouillon en localStorage (créa / onboarding). Défaut `true` ; le tableau de bord V2 passe `false`. */
  persist?: boolean;
  /**
   * Bloquer la validation sur les documents / dates d'expiration requis. Défaut `true` (créa). Le
   * tableau de bord V2 passe `false` : sauvegarde partielle autorisée (les manques sont signalés
   * visuellement, pas bloquants). Le contrôle de FORMAT des champs renseignés reste assuré côté
   * présentation via `validateSupplierSocietyFormat`.
   */
  requireCompleteness?: boolean;
};

/**
 * Hook headless de la société fournisseur (Bloomer) : porte tout l'état, les refs d'upload, les
 * brouillons localStorage et l'orchestration de validation extraits de `EditBloomerSociety`.
 *
 * Les règles métier (statut juridique → SIRET / TVA / pays, KBIS / URSSAF / RCP requis,
 * normalisation au submit) sont mono-sourcées dans `helpers/societyForm.ts` ; ce hook ne fait que
 * les câbler à l'état React. Il reste sans présentation pour qu'un écran legacy (atomes V1) ou une
 * future présentation V2 (primitives + Dropzone) partagent 100 % de la logique : tous deux n'ont
 * qu'à attacher les refs d'upload (qui exposent `validate()` / `scrollIntoView()`) et lire la config.
 */
export const useBloomerSocietyForm = ({
  society: bloomerSociety,
  onValidated,
  persist = true,
  requireCompleteness = true,
}: UseBloomerSocietyFormParams) => {
  const { t, i18n } = useTranslation(['bloomer', 'mission', 'society']);

  const initFile = (currentSociety?: ISociety, property?: string): any => {
    const keyOfSociety = property as keyof ISociety;
    return currentSociety?.[keyOfSociety]
      ? {
          path: path.basename(currentSociety?.[keyOfSociety] as string),
          url: currentSociety?.[keyOfSociety] as string,
        }
      : undefined;
  };

  const prepareSociety = (currentSociety?: ISociety) => {
    if (!currentSociety) return;
    return {
      portage: currentSociety.portage || false,
      recordId: currentSociety.recordId,
      name: currentSociety.name,
      siret: currentSociety.siret,
      status: currentSociety.status,
      KBIS: initFile(currentSociety, 'KBIS'),
      KbisExpirationDate: currentSociety.KbisExpirationDate,
      URSSAFVigilanceCertificate: initFile(currentSociety, 'URSSAFVigilanceCertificate'),
      URSSAFVCExpirationDate: currentSociety.URSSAFVCExpirationDate,
      RCPVigilanceCertificate: initFile(currentSociety, 'RCPVigilanceCertificate'),
      RCPExpirationDate: currentSociety.RCPExpirationDate,
      IBAN: currentSociety.IBAN,
      BIC: currentSociety.BIC,
      tva: currentSociety.tva,
      addressCountryCode: currentSociety.addressCountryCode,
    };
  };

  const UploadKBISRef = useRef<any>(null);
  const UploadURSSAFRef = useRef<any>(null);
  const UploadRCPRef = useRef<any>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [KBIS, setKBIS] = useState<any | undefined | null>(initFile(bloomerSociety, 'KBIS'));
  const [URSSAF, setURSSAF] = useState<any | undefined | null>(initFile(bloomerSociety, 'URSSAFVigilanceCertificate'));
  const [RCP, setRCP] = useState<any | undefined | null>(initFile(bloomerSociety, 'RCPVigilanceCertificate'));

  const [KbisExpirationDate, setKbisExpirationDate] = useState<string | undefined | null>(
    bloomerSociety ? bloomerSociety.KbisExpirationDate : undefined,
  );
  const [URSSAFVCExpirationDate, setURSSAFVCExpirationDate] = useState<string | undefined | null>(
    bloomerSociety ? bloomerSociety.URSSAFVCExpirationDate : undefined,
  );
  const [RCPExpirationDate, setRCPVCExpirationDate] = useState<string | undefined | null>(
    bloomerSociety ? bloomerSociety.RCPExpirationDate : undefined,
  );
  const [formSociety, setFormSociety] = usePersistentState<ISociety | undefined>(
    bloomerSociety?.recordId ? `bloomerSociety-${bloomerSociety.recordId}` : 'bloomerSociety',
    prepareSociety(bloomerSociety),
    persist,
  );
  const [onboardingDraftDates, setOnboardingDraftDates] = usePersistentState<
    { KbisExpirationDate?: string; URSSAFVCExpirationDate?: string; RCPExpirationDate?: string } | undefined
  >(LS_EXPIRATION_DATES_KEY, undefined, persist);
  const [expirationDateErrors, setExpirationDateErrors] = useState<{
    kbis?: string;
    urssaf?: string;
    rcp?: string;
  }>({});

  const supportIban = useMemo(() => {
    return hasIbanSupport(formSociety?.status, formSociety?.addressCountryCode || undefined);
  }, [formSociety?.status, formSociety?.addressCountryCode]);

  const resetFormSociety = (siret: string) => {
    setFormSociety((prev) => ({ recordId: prev?.recordId, siret, name: '' }));
    setKBIS(null);
    setKbisExpirationDate(null);
    setURSSAF(null);
    setURSSAFVCExpirationDate(null);
    setRCP(null);
    setRCPVCExpirationDate(null);
    setPreventText(true);
    setExpirationDateErrors({});
  };

  useEffect(() => {
    setFormSociety(prepareSociety(bloomerSociety));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bloomerSociety]);

  const isFirstTime = !bloomerSociety?.recordId;
  useEffect(() => {
    if (isFirstTime && onboardingDraftDates) {
      if (onboardingDraftDates.KbisExpirationDate) setKbisExpirationDate(onboardingDraftDates.KbisExpirationDate);
      if (onboardingDraftDates.URSSAFVCExpirationDate)
        setURSSAFVCExpirationDate(onboardingDraftDates.URSSAFVCExpirationDate);
      if (onboardingDraftDates.RCPExpirationDate) setRCPVCExpirationDate(onboardingDraftDates.RCPExpirationDate);
    }
  }, [isFirstTime]); // eslint-disable-line react-hooks/exhaustive-deps

  logger.debug('bloomerSociety', bloomerSociety);
  logger.debug('society', formSociety);
  const [preventText, setPreventText] = useState(false);

  const config: SupplierSocietyFormConfig = useMemo(
    () => getSupplierSocietyFormConfig(formSociety?.status, formSociety?.portage),
    [formSociety?.status, formSociety?.portage],
  );
  const { isMicroEntrepreneur, isEuropeanSociety, isForeignSociety, requiredVATNumber, showVATTextField, fieldMode } =
    config;
  const { kbisRequired, urssafRequired } = config.constraints;

  const statusInfos = { isEuropeanSociety, isForeignSociety, isMicroEntrepreneur };
  const societyKBISLabel = useMemo(
    () => getSocietyKBISLabel(statusInfos, i18n),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isEuropeanSociety, isForeignSociety, isMicroEntrepreneur, i18n.language],
  );
  const societyTaxesLabel = useMemo(
    () => getSocietyTaxesLabel(statusInfos, i18n),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isEuropeanSociety, isForeignSociety, isMicroEntrepreneur, i18n.language],
  );
  const societyInsuranceLabel = useMemo(
    () => getSocietyInsuranceLabel(statusInfos, i18n),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isEuropeanSociety, isForeignSociety, isMicroEntrepreneur, i18n.language],
  );

  const selectSocietyOption: Option[] = useMemo(
    () => getSupplierStatusOptions(formSociety?.portage),
    [formSociety?.portage],
  );

  const hasKbisDoc = useMemo(() => Boolean(KBIS || formSociety?.KBIS), [KBIS, formSociety?.KBIS]);
  const hasUrssafDoc = useMemo(
    () => Boolean(URSSAF || formSociety?.URSSAFVigilanceCertificate),
    [URSSAF, formSociety?.URSSAFVigilanceCertificate],
  );
  const hasRcpDoc = useMemo(
    () => Boolean(RCP || formSociety?.RCPVigilanceCertificate),
    [RCP, formSociety?.RCPVigilanceCertificate],
  );

  const expirationRequired = useMemo(
    () =>
      deriveExpirationRequired({
        kbisRequired,
        urssafRequired,
        hasKbis: hasKbisDoc,
        hasUrssaf: hasUrssafDoc,
        hasRcp: hasRcpDoc,
      }),
    [kbisRequired, urssafRequired, hasKbisDoc, hasUrssafDoc, hasRcpDoc],
  );
  const mustHaveKbisExpiration = expirationRequired.kbis;
  const mustHaveUrssafExpiration = expirationRequired.urssaf;
  const mustHaveRcpExpiration = expirationRequired.rcp;

  const shouldDisableExpirationDate = (date: Date) => {
    return isAfter(startOfToday(), date);
  };

  const validate = async (e: { preventDefault: () => void }): Promise<boolean> => {
    logger.debug('onSubmit', {
      isMicroEntrepreneur,
      societyKBISLabel,
      requiredVATNumber,
      showVATTextField,
      fieldMode,
      kbisRequired,
      urssafRequired,
      societyTaxesLabel,
      isForeignSociety,
      isEuropeanSociety,
      hasKbisDoc,
      hasUrssafDoc,
      hasRcpDoc,
      mustHaveKbisExpiration,
      mustHaveUrssafExpiration,
      mustHaveRcpExpiration,
      KbisExpirationDate,
      URSSAFVCExpirationDate,
      RCPExpirationDate,
      KBIS,
      URSSAF,
      RCP,
    });
    e.preventDefault();
    // Complétude : uniquement en mode strict (créa / onboarding). En mode souple (tableau de bord V2),
    // on autorise l'enregistrement partiel — les documents/dates manquants sont signalés visuellement.
    if (requireCompleteness) {
      if (!UploadKBISRef.current.validate()) {
        UploadKBISRef.current?.scrollIntoView();
        return false;
      }
      if (!UploadURSSAFRef.current.validate()) {
        UploadURSSAFRef.current?.scrollIntoView();
        return false;
      }
      if (!UploadRCPRef.current.validate()) {
        UploadRCPRef.current?.scrollIntoView();
        return false;
      }
      if (mustHaveKbisExpiration && !KbisExpirationDate) {
        logger.warn('Validation failed: KBIS expiration date missing');
        setExpirationDateErrors((prev) => ({ ...prev, kbis: t('expirationDateMustBeInFuture', { ns: 'bloomer' }) }));
        return false;
      }
      if (mustHaveUrssafExpiration && !URSSAFVCExpirationDate) {
        logger.warn('Validation failed: URSSAF expiration date missing');
        setExpirationDateErrors((prev) => ({ ...prev, urssaf: t('expirationDateMustBeInFuture', { ns: 'bloomer' }) }));
        return false;
      }
      if (mustHaveRcpExpiration && !RCPExpirationDate) {
        logger.warn('Validation failed: RCP expiration date missing');
        setExpirationDateErrors((prev) => ({ ...prev, rcp: t('expirationDateMustBeInFuture', { ns: 'bloomer' }) }));
        return false;
      }
    }
    setExpirationDateErrors({});
    try {
      if (formSociety) {
        const composed: ISociety = {
          ...formSociety,
          KBIS,
          URSSAFVigilanceCertificate: URSSAF,
          RCPVigilanceCertificate: RCP,
          URSSAFVCExpirationDate: URSSAFVCExpirationDate ?? undefined,
          KbisExpirationDate: KbisExpirationDate ?? undefined,
          RCPExpirationDate: RCPExpirationDate ?? undefined,
        };
        setUploading(true);
        const normalized = normalizeSupplierSocietyForSubmit(composed);
        const updatedSociety = await onValidated(normalized);
        if (updatedSociety) {
          setFormSociety(prepareSociety(updatedSociety));
          if (isFirstTime) {
            try {
              // clean onboarding expiration dates localStorage
              setOnboardingDraftDates(undefined);
            } catch (error) {
              logger.error('Error removing onboarding expiration dates', { updatedSociety }, error as Error);
            }
            setOnboardingDraftDates(undefined);
            logger.info('Society saved with expiration dates');
          }
        }
        setUploading(false);
        setPreventText(false);
      }
    } catch (err) {
      logger.error('onSubmit error', { formSociety, error: err }, err as Error);
      setUploading(false);
    }
    return true;
  };

  return {
    // état du formulaire
    formSociety,
    setFormSociety,
    // fichiers (KBIS / URSSAF / RCP)
    KBIS,
    setKBIS,
    URSSAF,
    setURSSAF,
    RCP,
    setRCP,
    // dates d'expiration
    KbisExpirationDate,
    setKbisExpirationDate,
    URSSAFVCExpirationDate,
    setURSSAFVCExpirationDate,
    RCPExpirationDate,
    setRCPVCExpirationDate,
    // brouillon onboarding
    onboardingDraftDates,
    setOnboardingDraftDates,
    isFirstTime,
    // erreurs / UI transverse
    expirationDateErrors,
    setExpirationDateErrors,
    preventText,
    setPreventText,
    uploading,
    // refs impératives d'upload (validate / scrollIntoView)
    UploadKBISRef,
    UploadURSSAFRef,
    UploadRCPRef,
    // configuration dérivée (règles mono-sourcées) + libellés
    config,
    isMicroEntrepreneur,
    isEuropeanSociety,
    isForeignSociety,
    requiredVATNumber,
    showVATTextField,
    fieldMode,
    kbisRequired,
    urssafRequired,
    societyKBISLabel,
    societyTaxesLabel,
    societyInsuranceLabel,
    mustHaveKbisExpiration,
    mustHaveUrssafExpiration,
    mustHaveRcpExpiration,
    hasKbisDoc,
    hasUrssafDoc,
    hasRcpDoc,
    supportIban,
    selectSocietyOption,
    // helpers
    resetFormSociety,
    shouldDisableExpirationDate,
    validate,
  };
};

export type UseBloomerSocietyFormReturn = ReturnType<typeof useBloomerSocietyForm>;

export default useBloomerSocietyForm;
